
import { catchError, debounceTime, lastValueFrom, Subject, takeUntil, tap } from "rxjs";
import { AjaxError,  } from "rxjs/ajax";
import { createEffect, createResource, createSignal, For, Show } from "solid-js";

import { FileUploadSelector } from "./FileUploadSelector";
import { InputField } from "./InputField";
import { Api, useApi, type ApiResponse } from "../util/api";
import type { UtdoFile } from "../types";
import { panic } from "../util/panic";

function uploadWithProgress$(api: Api, url: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return api.fetch<ApiResponse<boolean>>({
        url: url,
        method: "POST",
        body: formData,
        withCredentials: true,
        includeUploadProgress: true,
    }).pipe(
        catchError((caught) => {
            if (caught instanceof AjaxError && typeof caught.response?.error === "string") {
                throw new Error(caught.response.error);
            }
            throw caught;
        })
    );
};

export function Files() {
    const api = useApi();

    const cancelSubject$ = new Subject<void>();

    const [ directory, setDirectory ] = createSignal("");
    const [ files, { refetch } ] = createResource(
        directory,
        async (dir) => {
            dir = dir.trim();
            if (dir.length === 0) {
                return [] as UtdoFile[];
            }
            try {
                return await lastValueFrom(api.selectFiles(directory()))
            } catch (err: any) {
                panic(err);
                return [] as UtdoFile[];
            }
        },
        { initialValue: [] as UtdoFile[] },
    )
    const [ filesToUpload, setFilesToUpload ] = createSignal<File[]>([]);
    const [ fileToUpload, setFileToUpload ] = createSignal("");
    const [ rename, setRename ] = createSignal("");
    const [ progress, setProgress ] = createSignal("0%");
    const [ showDialog, setShowDialog ] = createSignal(false);
    
    createEffect(() => {
        const name = rename().trim();
        if (name.length === 0) {
            renameDialogRef.close();
            return;
        }
        renameDialogRef.showModal();
    })

    let directoryRef!: HTMLSelectElement;
    let dialogRef!: HTMLDialogElement;
    let renameDialogRef!: HTMLDialogElement;
    let renameRef!: HTMLInputElement;

    createEffect(() => {
        if (!showDialog()) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    })

    createEffect(async () => {
        const fs = filesToUpload();
        if (fs.length === 0) {
            return;
        }
        const errors: string[] = [];
        for (let i = 0; i < fs.length; i++) {
            const f = fs[i];
            setFileToUpload(`${f.name} (${i+1}/${fs.length})`);
            try {
                await lastValueFrom(uploadWithProgress$(api, `/api/files/${directory()}/`, f).pipe(
                    debounceTime(10),
                    tap((ev) => {
                        const percentage = ((ev.loaded / ev.total) * 100).toFixed(2);
                        setProgress(`${percentage}%`);
                    }),
                    takeUntil(cancelSubject$)
                ));
            } catch (err: any) {
                errors.push(err?.message ?? err);
            }
        }
        if (errors.length > 0) {
            panic(`Les erreurs suivantes sont survenues :\n${errors.map((e) => `- ${e}`).join("\n")}`)
        }
        setFilesToUpload([]);
        setShowDialog(false);
        refetch();
    })

    const addFiles = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setShowDialog(true);
    };

    const changeDirectory = (_: Event) => {
        setDirectory(directoryRef.value);
    };

    const cancelRename = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setRename("");
    };

    const cancelUpdater = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        cancelSubject$.next();
    };
    
    const deleteFile = (filename: string) => async (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (!confirm(`Vous allez supprimer le fichier "${filename}".\nContinuer ?`)) {
            return;
        }
        try {
            await lastValueFrom(api.deleteFile(directory(), filename));
        } catch (err: any) {
            panic(err);
        }

        refetch();
    };

    const renameFile = (filename: string) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setRename(filename);
    };

    const renameSubmit = async (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        try {
            await lastValueFrom(api.renameFile(directory(), rename(), renameRef.value.trim()));
        } catch (err: any) {
            panic(err);
        }
        setRename("");
        refetch();
    };

    const uploaderCancel = () => setShowDialog(false);
    const uploaderSubmit = (files: File[]) => setFilesToUpload(files);

    return <section id="admin-files">
        <h2>Fichiers</h2>
        <div>
            <div class="dir-select">
                <select ref={directoryRef} onChange={changeDirectory}>
                    <option value="">Aucun</option>
                    <option value="images">Images</option>
                    <option value="espace-pro">Espace-Pro</option>
                </select>
                <button onClick={addFiles} disabled={directory() === ""}>Ajouter des fichiers</button>
            </div>
            <ul>
                <For each={files()}>
                    {(file) => <li>
                        <a class="name" target="_blank"
                            href={`../fichiers/${directory()}/${file.name}`}>{file.name}</a>
                        <span class="size">{file.size}</span>
                        <span class="lastmod">{file.lastmod}</span>
                        <span>
                            <a href="#" class="icon" onClick={renameFile(file.name)}>save_as</a>
                            <a href="#" class="icon" onClick={deleteFile(file.name)}>delete</a>
                        </span>
                    </li>}
                </For>
            </ul>
        </div>
        <dialog ref={dialogRef} class="progress-dialog">
            <Show when={filesToUpload().length > 0} fallback={
                <FileUploadSelector
                    validate={uploaderSubmit}
                    cancel={uploaderCancel}
                    />
                }>
                <form>
                    <div class="progress">
                        <div style={{width: progress()}}>
                        </div>
                        <span>
                            Envoi de {fileToUpload()}
                        </span>
                    </div>
                    <div class="buttons">
                        <button onClick={cancelUpdater}>Annuler</button>
                    </div>
                </form>
            </Show>
        </dialog>
        <dialog ref={renameDialogRef}>
            <form onSubmit={renameSubmit} autocomplete="off">
                <InputField
                    ref={renameRef}
                    type="text"
                    id="files-rename"
                    label="Nom de fichier"
                    value={rename()}
                    />
                <div class="actions">
                    <input type="submit" class="accent" value="Valider"/>
                    <button onClick={cancelRename}>Annuler</button>
                </div>
            </form>
        </dialog>
    </section>;
}
